import { useMemo, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { GenieAvatar } from "@/components/GenieAvatar";
import { useUserAgents } from "@/hooks/agent";
import { useBrain } from "@/contexts/ui/brain";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { cn } from "@/lib/utils";
import type { Agent } from "@/types/agent/agent";

const getBrandColor = (agent: Agent) =>
  agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;

const getAvatarProps = (agent: Agent, size: number) => ({
  size,
  color: getBrandColor(agent),
  secondaryColor: agent.branding?.secondaryColor ?? null,
  gradientEnabled: agent.branding?.gradientEnabled,
  gradientAngle: agent.branding?.gradientAngle,
  customAvatarUrl: agent.branding?.logoUrl || agent.branding?.customAvatarUrl || null,
  hasCustomBranding: Boolean(
    agent.branding &&
      (agent.branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
        agent.branding.secondaryColor ||
        agent.branding.customAvatarUrl ||
        agent.branding.gradientEnabled),
  ),
});

interface GenieScopeSelectorProps {
  isMobile?: boolean;
}

const GenieScopeSelector = ({ isMobile }: GenieScopeSelectorProps) => {
  const { scope, setScope } = useBrain();
  const { data: agents = [], isLoading } = useUserAgents(100);
  const [open, setOpen] = useState(false);

  // Resolve current scope to an Agent (URLs use either DB id or elevenlabs_id)
  const scopedAgent = useMemo(() => {
    if (!scope?.genieId) return null;
    return agents.find(
      (a) => a.id === scope.genieId || a.elevenlabs_id === scope.genieId,
    ) ?? null;
  }, [agents, scope?.genieId]);

  const handleSelect = (agent: Agent | null) => {
    if (!agent) {
      setScope(null);
    } else {
      setScope({
        genieId: agent.elevenlabs_id || agent.id,
        genieName: agent.name,
      });
    }
    setOpen(false);
  };

  // Hide selector entirely if user has no genies — no scope to pick.
  if (!isLoading && agents.length === 0) return null;

  const brand = scopedAgent ? getBrandColor(scopedAgent) : "#4E9CFF";
  const displayName = scopedAgent ? scopedAgent.name : "All Genies";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border bg-white px-2 py-1 transition-all hover:shadow-sm",
            "max-w-[180px] sm:max-w-[260px]",
          )}
          style={{
            borderColor: scopedAgent ? `${brand}55` : "#E5EAF0",
          }}
          aria-label={`Your Genie scope: ${displayName}. Click to change.`}
        >
          {scopedAgent ? (
            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded-full">
              <GenieAvatar {...getAvatarProps(scopedAgent, 20)} />
            </span>
          ) : (
            <span
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
              style={{ background: "linear-gradient(135deg, #4E9CFF, #FF4DB8)" }}
            >
              All
            </span>
          )}
          <span
            className="truncate text-xs font-semibold"
            style={{ color: scopedAgent ? brand : "#222B45" }}
          >
            {displayName}
          </span>
          <ChevronDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={cn("p-0", isMobile ? "w-[calc(100vw-2rem)]" : "w-72")}
      >
        <Command>
          {agents.length > 6 && (
            <CommandInput placeholder="Search genies..." className="text-sm" />
          )}
          <CommandList className="max-h-72">
            <CommandEmpty>No genies found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all genies"
                onSelect={() => handleSelect(null)}
                className="flex items-center gap-2.5 cursor-pointer"
              >
                <span
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                  style={{ background: "linear-gradient(135deg, #4E9CFF, #FF4DB8)" }}
                >
                  All
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">All Genies</div>
                  <div className="text-[11px] text-gray-500">Answer across every genie</div>
                </div>
                {!scope && <Check className="h-4 w-4 text-genie-primary" />}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Your Genies">
              {agents.map((agent) => {
                const id = agent.elevenlabs_id || agent.id;
                const isSelected =
                  scope?.genieId === agent.id || scope?.genieId === agent.elevenlabs_id;
                const agentBrand = getBrandColor(agent);
                return (
                  <CommandItem
                    key={id}
                    value={`${agent.name} ${id}`}
                    onSelect={() => handleSelect(agent)}
                    className="flex items-center gap-2.5 cursor-pointer"
                  >
                    <span
                      className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border"
                      style={{ borderColor: `${agentBrand}55` }}
                    >
                      <GenieAvatar {...getAvatarProps(agent, 28)} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div
                        className="truncate text-sm font-semibold"
                        style={{ color: isSelected ? agentBrand : "#111827" }}
                      >
                        {agent.name}
                      </div>
                      {agent.description && (
                        <div className="truncate text-[11px] text-gray-500">
                          {agent.description}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="h-4 w-4" style={{ color: agentBrand }} />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default GenieScopeSelector;
